import { DvToolbarAction } from '@devinco/ui/grid';
import 'ag-grid-enterprise';
import {
  Component,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import { take, tap } from 'rxjs';
import { UserGridService } from './user-grid.service';
import { DialogService } from '@core/services/dialog.service';
import { UserDto } from '@shared/interfaces/user.model';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { RestApiService } from '@core/services/rest-api.service';
import { TranslateService } from '@ngx-translate/core';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';

@Component({
  selector: 'app-users-grid',
  templateUrl: './users-grid.component.html',
  styleUrls: ['./users-grid.component.scss'],
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UsersGridComponent implements OnInit {
  readonly gridOptionsService = inject(GridOptionsService);
  private splitterEvents = inject(DvSplitterEventsService);
  private readonly userGridService = inject(UserGridService);
  private dialog = inject(DialogService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private oidc = inject(OidcSecurityService);
  private api = inject(RestApiService);
  private translate = inject(TranslateService);
  private snackbar = inject(DvSnackbarService);

  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions;

  private selectedNode?: IRowNode<UserDto>;
  private canDelete: boolean;
  private canEdit: boolean;

  ngOnInit(): void {
    this.gridOptions = this.gridOptionsService.clientSide(
      this.userGridService.colDefs,
      Entity.User
    );
    this.actions.set(
      this.userGridService.getGridActions(
        this.onSendLoginInformationClick,
        this.onEditClick
      )
    );
    this.handleAccessRestrictions();
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.oidc
      .getUserData()
      .pipe(take(1))
      .subscribe(({ email }) => {
        this.selectedNode = api.getSelectedNodes()[0] as
          | IRowNode<UserDto>
          | undefined;
        const isSameUser = email === this.selectedNode?.data?.Username;

        this.actions.update(actions =>
          actions.map(action => {
            if (action.icon === 'delete') {
              return {
                ...action,
                disabled: isSameUser || !this.canDelete || !this.selectedNode,
              };
            }
            if (action.icon === 'edit') {
              return { ...action, disabled: !this.selectedNode };
            }
            if (action.icon === 'email') {
              return {
                ...action,
                disabled: !this.canEdit || !this.selectedNode,
              };
            }
            return action;
          })
        );
      });
  }

  private onEditClick = (): void =>
    this.splitterEvents.isSplitterOpened$.next(true);

  private onSendLoginInformationClick = (): void => {
    const { Username, UserID, UserDescription } = this.selectedNode!
      .data as UserDto;
    this.dialog.open({
      size: 'sm',
      title: 'User.Dialog.UserInformation.Title',
      contentText: this.translate.instant(
        'User.Dialog.UserInformation.EmailText',
        {
          userInfo: `${UserDescription ? UserDescription + ',' : ''} ${Username}`,
        }
      ),
      confirmText: 'General.Send',
      cancelText: 'General.Cancel',
      onConfirm: () => {
        this.api
          .putCustom(`User/sendlogin/${UserID}`)
          .pipe(
            tap(() => {
              this.snackbar.open(
                this.translate.instant('User.LoginInfoSent'),
                SnackBarConfig.Success
              );
            })
          )
          .subscribe();
      },
    });
  };

  private handleAccessRestrictions(): void {
    this.permissionsService
      .getUserRolesAndPermissions()
      .pipe(take(1))
      .subscribe(data => {
        if (data.Permissions.includes(RolesAndPermissions.UserDelete)) {
          this.canDelete = true;
        }
        if (data.Permissions.includes(RolesAndPermissions.UserEdit)) {
          this.canEdit = true;
        }
      });
  }
}
